/**
 * @file services/models.ts
 * @description Service for handling models (fulfillments) data for Model Reviewer/Preparer Dashboard
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { UserFulfillmentRequest } from 'api/models/UserFulfillmentRequest';
import { ApiService } from 'api/services/ApiService';
import { RootStateType } from 'dashboard409a/states/store';

// Query keys for models operations
export const ModelsQueryKeys = {
  MODELS_LIST: 'models-list',
} as const;

/**
 * Hook to get models list for all companies where user is reviewer/preparer
 * @param filterType - Filter by 'current' or 'past' models
 * @param userIds - Optional array of user IDs. If not provided, uses current user ID from Redux store
 * @param page - Optional page number for pagination - Note: Currently not used by backend
 */
export const useModelsList = (
  filterType: 'current' | 'past' = 'current',
  preparerUserIds?: number[],
  reviewerUserIds?: number[],
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  page?: number // Reserved for future pagination support
) => {
  // Get current user ID from Redux store
  const currentUserId = useSelector((state: RootStateType) => state.user.id);

  // Create stable string representation of user IDs for dependency comparison
  // Combines both preparer and reviewer IDs into a single stable key
  const userIdsKey = useMemo(() => {
    let preparerIds: number[] = [];
    if (preparerUserIds && preparerUserIds.length > 0) {
      preparerIds = [...preparerUserIds].sort((a, b) => a - b);
    } else if (currentUserId) {
      preparerIds = [currentUserId];
    }

    let reviewerIds: number[] = [];
    if (reviewerUserIds && reviewerUserIds.length > 0) {
      reviewerIds = [...reviewerUserIds].sort((a, b) => a - b);
    } else if (currentUserId) {
      reviewerIds = [currentUserId];
    }

    // Combine both arrays, remove duplicates, and create stable key
    const allIds = [...new Set([...preparerIds, ...reviewerIds])].sort((a, b) => a - b);

    // Create a key that distinguishes between preparer and reviewer IDs
    // Format: "preparer:1,2,3|reviewer:4,5,6"
    const preparerKey = preparerIds.length > 0 ? `preparer:${preparerIds.join(',')}` : '';
    const reviewerKey = reviewerIds.length > 0 ? `reviewer:${reviewerIds.join(',')}` : '';

    return [preparerKey, reviewerKey].filter(Boolean).join('|') || allIds.join(',');
  }, [preparerUserIds, reviewerUserIds, currentUserId]);

  // Memoize targetUserIds to avoid unnecessary recalculations
  const targetPreparerIds = useMemo(() => {
    if (preparerUserIds && preparerUserIds.length > 0) {
      return preparerUserIds;
    }
    if (currentUserId) {
      return [currentUserId];
    }
    return [];
  }, [preparerUserIds, currentUserId]);

  // Memoize targetUserIds to avoid unnecessary recalculations
  const targetReviewerIds = useMemo(() => {
    if (reviewerUserIds && reviewerUserIds.length > 0) {
      return reviewerUserIds;
    }
    if (currentUserId) {
      return [currentUserId];
    }
    return [];
  }, [reviewerUserIds, currentUserId]);

  const preparerIds = useMemo(
    () => targetPreparerIds.filter((id): id is number => id !== undefined),
    [targetPreparerIds]
  );
  const reviewerIds = useMemo(
    () => targetReviewerIds.filter((id): id is number => id !== undefined),
    [targetReviewerIds]
  );
  // Create stable query key - sort and join only once, memoized for performance
  const queryKey = useMemo(() => [ModelsQueryKeys.MODELS_LIST, filterType, userIdsKey], [userIdsKey, filterType]);

  return useQuery({
    queryKey,
    queryFn: async () => {
      if (preparerIds.length === 0 && reviewerIds.length === 0) {
        throw new Error('At least one preparer or reviewer ID is required');
      }

      // Use the POST API that accepts separate preparer and reviewer IDs
      const requestBody: UserFulfillmentRequest = {
        preparer_ids: preparerIds,
        reviewer_ids: reviewerIds,
        status_filter:
          filterType === 'current'
            ? UserFulfillmentRequest.status_filter.CURRENT
            : UserFulfillmentRequest.status_filter.PAST,
      };

      const response = await ApiService.apiDashboardUserModelsCreate(requestBody);

      return response;
    },
    enabled: preparerIds.length > 0 || reviewerIds.length > 0,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache for 30 minutes
    placeholderData: previousData => previousData, // Keep previous data while loading new data
  });
};
